import { useCallback } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";
import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdatePurchaseProBillingTicketType,
} from "@/components/features/settings/billing/model/use-case";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";

import { PurchaseTicketDialog } from "../../../../_shared/dialog";
import { SettingsBillingSection } from "../../../../_shared/section";
import { useTicketList } from "../../../../_shared/ticket-list/ticket-list";
import { PurchaseDialogContent } from "../current-ticket/components/purchase-dialog-content";
import { useCurrentTicketState } from "../current-ticket/hooks/use-current-ticket-state";

import type { TicketListItemType } from "../../../../_shared/ticket-list/ticket-list";

type Props = {
	/**
	 * Proチケットを使用しているかどうか
	 */
	isUsingPro: boolean;
	ticketProListAvailableSingleBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableSingleBilling"];
	billingStatus: BillingStatus;
	onPurchaseProBillingTicket: OnUpdatePurchaseProBillingTicketType;
	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;
};

export const ProTicketCurrentStatusSection = ({
	isUsingPro,
	ticketProListAvailableSingleBilling,
	billingStatus,
	onPurchaseProBillingTicket,
	openBillingSettingDialog,
	handleOpenChange: _handleOpenChange,
	activateBillingSettingDialog,
	deactivateBillingSettingDialog,
}: Props) => {
	const { isStopRecurringProBilling, onUpdateIsStopRecurringProBilling, onResetIsStopRecurringProBilling } =
		useCurrentTicketState({ billingStatus });

	// チケットのrender hooks を初期化（チケットのリストコンポーネントと選択されているチケットIDを返す）
	const { selectedTicketID, ticketList_Component, handleChangeSelectedTicketID } = useTicketList({
		accentColor: "yellow",
		ticketList: ticketProListAvailableSingleBilling.tickets.map((ticket) => {
			return {
				id: ticket?.hotelProBillingTicketID ?? "",
				name: ticket?.name || "",
				validMonth: ticket?.validMonth || 0,
				price: ticket?.price || { amount: 0, currencyType: CurrencyType.Jpy },
			} satisfies TicketListItemType;
		}),
	});

	const handleOpenChange = useCallback(
		(open: boolean) =>
			_handleOpenChange("PRO")(
				setTimeout(() => {
					handleChangeSelectedTicketID("");
					onResetIsStopRecurringProBilling();
				}, 100),
			)(open),
		[_handleOpenChange, handleChangeSelectedTicketID, onResetIsStopRecurringProBilling],
	);

	const handleClick = useCallback(() => {
		activateBillingSettingDialog("PRO");
	}, [activateBillingSettingDialog]);

	const isValid = !!selectedTicketID;

	const handlePurchase = useCallback(async () => {
		if (!selectedTicketID) {
			return;
		}

		deactivateBillingSettingDialog("PRO");

		const result = await onPurchaseProBillingTicket({
			defaultProBillingTicketID: selectedTicketID,
			isStopRecurringProBilling,
		});

		if (result) {
			notifyUserEventInfo({
				title: "定額チケットを購入しました",
			});
		}
	}, [deactivateBillingSettingDialog, onPurchaseProBillingTicket, selectedTicketID, isStopRecurringProBilling]);

	const inactiveTextStyle = "text-zinc-400";
	const activeTextStyle = "font-semibold";

	return (
		<>
			<SettingsBillingSection title="現在のステータス">
				<div className="col-span-3 flex items-center gap-x-2 text-lg tracking-wider">
					<p className={cn(isUsingPro ? inactiveTextStyle : activeTextStyle)}>従量課金</p>
					<p>/</p>
					<p className={cn(isUsingPro ? activeTextStyle : inactiveTextStyle)}>定額課金</p>
				</div>

				{!isUsingPro && (
					<div className="col-span-1 flex justify-end">
						<Button type="button" variant="outline" onClick={handleClick}>
							定額課金に切り替える
						</Button>
					</div>
				)}
				{!isUsingPro && (
					<p className="col-span-4 text-sm text-zinc-400">※ 予約1件につき3％ の手数料が発生します</p>
				)}
			</SettingsBillingSection>

			<PurchaseTicketDialog
				title="予約手数料 | 定額チケットの購入"
				open={openBillingSettingDialog.PRO}
				handleOpenChange={handleOpenChange}
			>
				<PurchaseDialogContent
					ticketListComponent={ticketList_Component}
					isStopRecurringProBilling={isStopRecurringProBilling}
					onUpdateIsStopRecurringProBilling={onUpdateIsStopRecurringProBilling}
					isValid={isValid}
					onPurchase={handlePurchase}
				/>
			</PurchaseTicketDialog>
		</>
	);
};

ProTicketCurrentStatusSection.displayName = "ProTicketCurrentStatusSection";
